import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Glyphicon } from 'react-bootstrap';
import { SearchBar } from 'axway-react-ui';
import browserHistory from 'components/utility/history';
import ToolPanel from './ToolPanel/ToolPanel';
import CollapsiblePanel from './CollapsiblePanel';

class FlowToolPanel extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.toggleFlowToolPanel = this.toggleFlowToolPanel.bind(this);

		this.empty = (
			<div className="empty">
				<Glyphicon glyph=" gg icon-GG-info" />
				<span>{this.intl.formatMsg('No API orchestration tools')}</span>
			</div>
		);
		this.state = {
			expanded: true
		};
	}

	toggleFlowToolPanel () {

		this.setState((prevState) => ({
			expanded: !prevState.expanded
		}));
	}

	buildDrawers() {
		const {
			nodehandlers: allNodehandlers,
			triggerDefinitions,
			filterFlowTriggers,
			showFlowTriggers
		} = this.props;

		// Filter out deprecated node handlers unless display is requested.
		const nodehandlers = this.props.showDeprecated ? allNodehandlers
			: Object.keys(allNodehandlers).reduce(
				(col, cur) => {
					const nodehandler = this.props.nodehandlers[cur];
					if (!nodehandler.deprecated) {
						col[cur] = nodehandler;
					}
					return col;
				}, {});

		const handlersByCategory = {};

		Object.keys(nodehandlers)
			.map(key => ({ key, ...nodehandlers[key] }))
			.forEach(nodehandler => {
				const category = nodehandler.category || 'other';
				handlersByCategory[category] = handlersByCategory[category] || [];
				handlersByCategory[category].push(nodehandler);
			});

		const toolDrawers = Object.keys(handlersByCategory).reduce((drawers, category) => {
			const handlersInCategory = handlersByCategory[category];
			drawers.push(
				<ToolPanel.Drawer
					toolPanelExpanded={this.state.expanded}
					// 'group' prefix prevents clashing with hardcoded `triggers`
					// key used for Flow-Triggers section
					key={`group.${category}`}
					category={category}
					items={handlersInCategory}
					draggable={this.props.draggable}
				/>
			);
			return drawers.sort((a, b) => a.key.localeCompare(b.key));
		}, []);

		if (showFlowTriggers) {
			const items = [];
			for (const triggerTypeId in triggerDefinitions) {
				if (filterFlowTriggers.includes(triggerTypeId)) {
					continue;
				}
				items.push({
					key: `triggers-${triggerTypeId}`,
					category: 'triggers',
					triggerType: triggerTypeId,
					name: triggerDefinitions[triggerTypeId].name,
					icon: triggerDefinitions[triggerTypeId].icon
				});
			}
			if (items.length) {
				toolDrawers.unshift(<ToolPanel.Drawer
					toolPanelExpanded={this.state.expanded}
					key="triggers"
					type="triggers"
					category={this.intl.formatMsg('Flow-Triggers')}
					items={items}
					draggable={this.props.draggable}
				/>);
			} else if (this.state.expanded) {
				const location = (
					<a
						href={`${window.$conf.prefix}/project/plugins`}
						onClick={this.navigateToPlugins}
					>
						{this.intl.formatMsg('Plugins')}
					</a>
				);
				const panelContent = (
					<div className="no-triggers-message">
						{this.intl.formatMsg('No flow-triggers are installed.')}
						{this.intl.formatHTMLMsg('You can install from {location}.', 'fmt_no_triggers_msg', { location })}
					</div>
				);

				toolDrawers.unshift(<ToolPanel.Drawer
					toolPanelExpanded={this.state.expanded}
					key="triggers"
					type="triggers"
					category={this.intl.formatMsg('Flow-Triggers')}
					content={panelContent}
					draggable={this.props.draggable}
				/>);
			}
		}

		return toolDrawers;
	}

	navigateToPlugins(ev) {
		ev.stopPropagation();
		ev.preventDefault();
		browserHistory.push(`${window.$conf.prefix}/project/plugins`);
	}

	render() {
		const { onSearch } = this.props;
		const drawers = this.buildDrawers();
		const flowPanelClassName = this.state.expanded ? 'flow-tool-panel' : 'collapsed-tool-panel flow-tool-panel';
		return (
			<div className={flowPanelClassName}>
				{ onSearch && drawers && drawers.length && (
					<SearchBar
						placeholder={this.intl.formatMsg('Search')}
						onChange={onSearch}
						throttle={50}
					/>
				)}
				<CollapsiblePanel
					header={this.intl.formatMsg('Flow-Nodes')}
					expanded={this.state.expanded}
					onToggle={this.toggleFlowToolPanel}
					align="left"
				>
					<ToolPanel
						empty={this.empty}
					>
						{drawers}
					</ToolPanel>
				</CollapsiblePanel>
			</div>
		);
	}
}

FlowToolPanel.propTypes = {
	/**
	 * The node handlers
	 */
	nodehandlers: PropTypes.object.isRequired,
	/**
	 * The flow-trigger definitions
	 */
	triggerDefinitions: PropTypes.object.isRequired,
	/**
	 * swith to enable/disable draggability of the nodehandlers. Enabled by default
	 */
	draggable: PropTypes.bool,
	/**
	 * The search callback
	 */
	onSearch: PropTypes.func,
	/**
	 * Flag to indicate if deprecated nodehandlers should be displayed.
	 */
	showDeprecated: PropTypes.bool,
	/**
	 * Flag to indicate that the Flow-Triggers should not be rendered at all.
	 */
	showFlowTriggers: PropTypes.bool,
	/**
	 * A list of flowTriggerTypeId to filter from the Flow-Triggers.
	 */
	filterFlowTriggers: PropTypes.arrayOf(PropTypes.string)
};

FlowToolPanel.defaultProps = {
	showDeprecated: false,
	showFlowTriggers: true,
	filterFlowTriggers: []
};

export default injectIntl(FlowToolPanel);
